import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import leftBackgroundImage from '/img/project_authSideImage.jpg';

/* -------- Redux -------- */
import { useDispatch, useSelector } from "react-redux";
import { fetchParameters, registerUserRequest, clearUserSuccess, clearUserErrors } from "../../store/actions";
/* ------------------------- */

/* -------- packages -------- */
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Spinner,
} from "reactstrap";
/* ------------------------- */

/* -------- components -------- */
import DynamicForm from "../../components/ui/Form";
import ErrorHandler from "../../components/ui/Error/ErrorHandler";
/* ------------------------- */

/* -------- Intern functions -------- */
import { getActivities } from "../../utils/utils";
/* ------------------------- */

export default function Register() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const parameters = useSelector(state => state.Parameters);
  document.title = `${t('register')} | ${parameters.appName}`;
  
  const usersState = useSelector(state => state.Users);

  const [signUpValues, setSignUpValues] = useState({
    fullName: '',
    role: '',
    email: '',
    phone: '',
    company: '',
    activity: '',
    siret: '',
    address: '',
    postalCode: '',
    city: '',
    password: '',
    passwordRepeat: '',
  });

  const [signUpState, setSignUpState] = useState(false);

  useEffect(() => {
    if (!parameters.logoIcon || !parameters.logo) {
      fetch('/parameters.json')
        .then(response => response.json())
        .then((data) => {
          if(data) {
            dispatch(fetchParameters(data));
          }
        })
        .catch((error) => {
          console.error('Error loading parameters:', error);
        });
    }
  }, [dispatch]);

  useEffect(() => {
    if (signUpState) {
      setSignUpState(false);

      if (usersState.registerSuccess) {
        toast.success(t('registrationSuccessful'));
        toast.info(t('activationEmailSent'));
        dispatch(clearUserSuccess());
        
        // Revert to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    }
  }, [usersState.registerSuccess, usersState.errorUser, navigate, dispatch, t]);

  // Define fields for signup form
  const activityOptions = getActivities().map(activity => ({
    id: activity,
    label: activity,
  }));

  const signupFields = [
    { key: 'fullName', type: 'letter', required: true, placeholder: 'DUPONT Jean' },
    { key: 'role', type: 'text', required: true, placeholder: 'Employé' },
    { key: 'email', type: 'email', required: true, placeholder: 'example@' + parameters.appName + '.com' },
    { key: 'phone', type: 'text', required: true, regex: /^(\+33\s?\d|0\d)(\s?\d{2}){4}$/, placeholder: '+33 6 12 34 56 78', errorMessage: t('phoneError') },
    { key: 'company', type: 'text', required: true, placeholder: 'ElectStart' },
    { key: 'activity', type: 'select', options: activityOptions, required: true, placeholder: t(activityOptions[0]?.label || '') },
    { key: 'siret', type: 'text', required: true, regex: /^[0-9]{14}$/, placeholder: '75007500750075', errorMessage: t('siretError') },
    { key: 'address', type: 'text', required: true, placeholder: 'Av. Gustave Eiffel' },
    { key: 'postalCode', type: 'text', required: true, regex: /^[0-9]{5}$/, errorMessage: t('postalCodeError'), placeholder: '75007' },
    { key: 'city', type: 'letter', required: true, placeholder: 'Paris' },
    { key: 'password', type: 'password', required: true, regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/, message: t('STRONG_PASSWORD'), placeholder: '••••••••••' },
    { key: 'passwordRepeat', type: 'password', required: true, placeholder: '••••••••••' },
  ];

  // Define layout disposition
  const disposition = [
    ['fullName', 'role'],
    ['email', 'phone'],
    ['company', 'activity'],
    ['siret'],
    ['address'],
    ['postalCode', 'city'],
    ['password', 'passwordRepeat']
  ];

  if (!parameters.logoIcon) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 authenticationPage">
        <Spinner color="primary" />
      </div>
    );
  }

  ErrorHandler(usersState.errorUser, clearUserErrors);

  return (
    <Container fluid className="p-0 m-0 vh-100 overflow-auto d-flex flex-column authenticationPage">
      <Row className="h-100 m-0">
        <Col lg={8} className="d-none d-lg-block p-0">
          <div className="auth-full-bg p-0 h-100">
            <div className="d-flex h-100 p-0">
              <img src={leftBackgroundImage} alt="" className="img-fluid" />
              <span className="logoLogin">
                <img src={parameters.logoDark} alt="logo" width={300} className="logo"/>
              </span>
            </div>
          </div>
        </Col>

        <Col lg={4} className="p-0 authenticationContainer h-100 d-flex flex-column justify-content-between flex-grow-1">
          <Row className="d-flex flex-column justify-content-center align-items-center h-25">
            <Col className="cardHeader mg-0 signUp">
              <Col lg={12} className="col p-0">
                <div className="text-primary">
                  <h5 className="text-primary">{t('welcome')}</h5>
                  <p>{t('registerPageText') + ' ' + parameters.appName}</p>
                </div>
              </Col>
            </Col>
          </Row>
          
          <Row className="d-flex flex-column align-items-center p-3 h-auto">
            <Card className="overflow-hidden">
              <CardBody className="pt-0">
                <div className="interactionContainer">
                  <DynamicForm
                    fields={signupFields}
                    initialValues={signUpValues} 
                    editFunction={registerUserRequest}
                    handleCancelFunction={() => navigate('/login')}
                    isEdit={false}
                    error={usersState.errorUser}
                    prefListName="user"
                    setInitialValues={setSignUpValues}
                    disposition={disposition}
                    withoutCancel={false}
                    isSignupForm={true}
                    isLoading={usersState.loadingUser} // ← CHANGEMENT ICI : loadingUser au lieu de loading
                    handleAfterSubmit={() => setSignUpState(true)}
                  />
                </div>
              </CardBody>
            </Card>
          </Row>

          <Row className="h-25">
            <div className="cardFooter">
              
            </div>
          </Row>
        </Col>
      </Row>

      <ToastContainer />
    </Container>
  );
}